import { useState, useEffect } from 'react'
import {
  Grid,
  Box,
  Typography,
  Switch,
  FormControlLabel,
  Divider,
  Tabs,
  Tab,
  TextField,
  MenuItem,
  IconButton,
  Chip,
  FormControl,
  InputLabel,
  Select,
  OutlinedInput,
  Checkbox,
  ListItemText,
  Alert,
} from '@mui/material'
import { Add, Edit, Delete, Person, Business, Security, Settings as SettingsIcon, Send, Refresh, Cancel } from '@mui/icons-material'
import { PageHeader, Card, Button, DataTable, Modal, StatusChip } from '@components/common'
import { useAuth } from '@context/AuthContext'
import api from '@services/api'
import { toast } from 'react-toastify'

// User Management Tab - Uses invitations for new users
const UsersTab = () => {
  const [users, setUsers] = useState([])
  const [invitations, setInvitations] = useState([])
  const [departments, setDepartments] = useState([])
  const [roles, setRoles] = useState([])
  const [loading, setLoading] = useState(true)
  const [editFormOpen, setEditFormOpen] = useState(false)
  const [inviteFormOpen, setInviteFormOpen] = useState(false)
  const [selectedUser, setSelectedUser] = useState(null)
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [activeSubTab, setActiveSubTab] = useState(0)
  const [editFormData, setEditFormData] = useState({
    department: '',
    job_title: '',
    phone_number: '',
    is_technician: false,
    role_ids: [],
  })
  const [inviteFormData, setInviteFormData] = useState({
    email: '',
    first_name: '',
    last_name: '',
    department: '',
    job_title: '',
    phone_number: '',
    is_technician: false,
    is_staff: false,
    role_ids: [],
  })
  const [saving, setSaving] = useState(false)

  const fetchData = async () => {
    try {
      setLoading(true)
      const [usersRes, invitesRes, deptsRes, rolesRes] = await Promise.all([
        api.get('/common/profiles/'),
        api.get('/auth/invitations/'),
        api.get('/common/departments/'),
        api.get('/common/roles/'),
      ])
      setUsers(usersRes.data.results || usersRes.data)
      setInvitations(invitesRes.data.results || invitesRes.data)
      setDepartments(deptsRes.data.results || deptsRes.data)
      setRoles(rolesRes.data.results || rolesRes.data)
    } catch (error) {
      console.error('Error fetching data:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [])

  const handleInviteUser = () => {
    setInviteFormData({
      email: '',
      first_name: '',
      last_name: '',
      department: '',
      job_title: '',
      phone_number: '',
      is_technician: false,
      is_staff: false,
      role_ids: [],
    })
    setInviteFormOpen(true)
  }

  const handleEdit = (profile) => {
    setSelectedUser(profile)
    setEditFormData({
      department: profile.department || '',
      job_title: profile.job_title || '',
      phone_number: profile.phone_number || '',
      is_technician: profile.is_technician || false,
      role_ids: profile.roles?.map((r) => r.id) || [],
    })
    setEditFormOpen(true)
  }

  const handleSaveEdit = async () => {
    setSaving(true)
    try {
      await api.patch(`/common/profiles/${selectedUser.id}/`, {
        department: editFormData.department || null,
        job_title: editFormData.job_title,
        phone_number: editFormData.phone_number,
        is_technician: editFormData.is_technician,
        role_ids: editFormData.role_ids,
      })
      toast.success('User updated successfully')
      setEditFormOpen(false)
      fetchData()
    } catch (error) {
      console.error('Error saving user:', error)
      const errorMessage = error.response?.data?.error
        || error.response?.data?.detail
        || 'Failed to update user. Please try again.'
      toast.error(errorMessage)
    } finally {
      setSaving(false)
    }
  }

  const handleSendInvitation = async () => {
    setSaving(true)
    try {
      await api.post('/auth/invitations/', {
        email: inviteFormData.email,
        first_name: inviteFormData.first_name,
        last_name: inviteFormData.last_name,
        department: inviteFormData.department || null,
        job_title: inviteFormData.job_title,
        phone_number: inviteFormData.phone_number,
        is_technician: inviteFormData.is_technician,
        is_staff: inviteFormData.is_staff,
        role_ids: inviteFormData.role_ids,
      })
      toast.success('Invitation sent successfully!')
      setInviteFormOpen(false)
      fetchData()
    } catch (error) {
      console.error('Error sending invitation:', error)
      const errorMessage = error.response?.data?.error
        || error.response?.data?.detail
        || error.response?.data?.email?.[0]
        || 'Failed to send invitation. Please try again.'
      toast.error(errorMessage)
    } finally {
      setSaving(false)
    }
  }

  const handleResendInvitation = async (invitation) => {
    try {
      await api.post(`/auth/invitations/${invitation.id}/resend/`)
      toast.success('Invitation resent successfully!')
    } catch (error) {
      toast.error('Failed to resend invitation.')
    }
  }

  const handleCancelInvitation = async (invitation) => {
    try {
      await api.post(`/auth/invitations/${invitation.id}/cancel/`)
      toast.success('Invitation cancelled.')
      fetchData()
    } catch (error) {
      toast.error('Failed to cancel invitation.')
    }
  }

  const handleDelete = async () => {
    try {
      await api.delete(`/common/users/${selectedUser.user.id}/`)
      setDeleteModalOpen(false)
      toast.success('User deleted successfully')
      fetchData()
    } catch (error) {
      console.error('Error deleting user:', error)
      const errorMessage = error.response?.data?.error
        || error.response?.data?.detail
        || 'Failed to delete user. Please try again.'
      toast.error(errorMessage)
    }
  }

  const userColumns = [
    {
      field: 'user',
      headerName: 'Name',
      renderCell: (value) => `${value?.first_name || ''} ${value?.last_name || ''}`.trim() || value?.username,
    },
    { field: 'user', headerName: 'Username', renderCell: (value) => value?.username },
    { field: 'user', headerName: 'Email', renderCell: (value) => value?.email || '-' },
    { field: 'department_name', headerName: 'Department', renderCell: (value) => value || '-' },
    { field: 'job_title', headerName: 'Job Title', renderCell: (value) => value || '-' },
    {
      field: 'is_technician',
      headerName: 'Technician',
      renderCell: (value) => value ? <Chip label="Yes" size="small" color="primary" /> : <Chip label="No" size="small" />,
    },
    {
      field: 'roles',
      headerName: 'Roles',
      renderCell: (value) => value?.map((r) => r.name).join(', ') || '-',
    },
    {
      field: 'actions',
      headerName: '',
      renderCell: (_, row) => (
        <Box display="flex" gap={1}>
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleEdit(row) }}>
            <Edit fontSize="small" />
          </IconButton>
          <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); setSelectedUser(row); setDeleteModalOpen(true) }}>
            <Delete fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ]

  const invitationColumns = [
    { field: 'email', headerName: 'Email' },
    {
      field: 'name',
      headerName: 'Name',
      renderCell: (_, row) => `${row.first_name || ''} ${row.last_name || ''}`.trim() || '-',
    },
    { field: 'department_name', headerName: 'Department', renderCell: (value) => value || '-' },
    { field: 'job_title', headerName: 'Job Title', renderCell: (value) => value || '-' },
    {
      field: 'status',
      headerName: 'Status',
      renderCell: (value) => (
        <Chip
          label={value}
          size="small"
          color={value === 'pending' ? 'warning' : value === 'accepted' ? 'success' : 'default'}
        />
      ),
    },
    {
      field: 'created_at',
      headerName: 'Sent',
      renderCell: (value) => new Date(value).toLocaleDateString(),
    },
    {
      field: 'actions',
      headerName: '',
      renderCell: (_, row) => row.status === 'pending' && (
        <Box display="flex" gap={1}>
          <IconButton
            size="small"
            title="Resend invitation"
            onClick={(e) => { e.stopPropagation(); handleResendInvitation(row) }}
          >
            <Refresh fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            color="error"
            title="Cancel invitation"
            onClick={(e) => { e.stopPropagation(); handleCancelInvitation(row) }}
          >
            <Cancel fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ]

  const pendingInvitations = invitations.filter(i => i.status === 'pending')

  return (
    <>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Tabs value={activeSubTab} onChange={(_, v) => setActiveSubTab(v)}>
          <Tab label={`Users (${users.length})`} />
          <Tab label={`Pending Invitations (${pendingInvitations.length})`} />
        </Tabs>
        <Button startIcon={<Send />} onClick={handleInviteUser}>Invite User</Button>
      </Box>

      {activeSubTab === 0 && (
        <DataTable
          columns={userColumns}
          data={users}
          loading={loading}
          searchPlaceholder="Search users..."
          emptyMessage="No users found"
        />
      )}

      {activeSubTab === 1 && (
        <>
          <Alert severity="info" sx={{ mb: 2 }}>
            Users will receive an email invitation and must set their own password when accepting.
          </Alert>
          <DataTable
            columns={invitationColumns}
            data={pendingInvitations}
            loading={loading}
            searchPlaceholder="Search invitations..."
            emptyMessage="No pending invitations"
          />
        </>
      )}

      {/* Invite User Modal */}
      <Modal
        open={inviteFormOpen}
        onClose={() => setInviteFormOpen(false)}
        title="Invite New User"
        maxWidth="sm"
        actions={
          <>
            <Button variant="outlined" onClick={() => setInviteFormOpen(false)}>Cancel</Button>
            <Button onClick={handleSendInvitation} loading={saving} startIcon={<Send />}>Send Invitation</Button>
          </>
        }
      >
        <Alert severity="info" sx={{ mb: 2 }}>
          The user will receive an email with a link to set up their account and create their own password.
        </Alert>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Email Address"
              type="email"
              value={inviteFormData.email}
              onChange={(e) => setInviteFormData({ ...inviteFormData, email: e.target.value })}
              required
              helperText="Invitation will be sent to this email"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="First Name"
              value={inviteFormData.first_name}
              onChange={(e) => setInviteFormData({ ...inviteFormData, first_name: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Last Name"
              value={inviteFormData.last_name}
              onChange={(e) => setInviteFormData({ ...inviteFormData, last_name: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Department</InputLabel>
              <Select
                value={inviteFormData.department}
                onChange={(e) => setInviteFormData({ ...inviteFormData, department: e.target.value })}
                label="Department"
              >
                <MenuItem value="">None</MenuItem>
                {departments.map((d) => (
                  <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Job Title"
              value={inviteFormData.job_title}
              onChange={(e) => setInviteFormData({ ...inviteFormData, job_title: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Phone Number"
              value={inviteFormData.phone_number}
              onChange={(e) => setInviteFormData({ ...inviteFormData, phone_number: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={inviteFormData.is_technician}
                  onChange={(e) => setInviteFormData({ ...inviteFormData, is_technician: e.target.checked })}
                />
              }
              label="Is Technician"
            />
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>Roles</InputLabel>
              <Select
                multiple
                value={inviteFormData.role_ids}
                onChange={(e) => setInviteFormData({ ...inviteFormData, role_ids: e.target.value })}
                input={<OutlinedInput label="Roles" />}
                renderValue={(selected) => roles.filter((r) => selected.includes(r.id)).map((r) => r.name).join(', ')}
              >
                {roles.map((role) => (
                  <MenuItem key={role.id} value={role.id}>
                    <Checkbox checked={inviteFormData.role_ids.includes(role.id)} />
                    <ListItemText primary={role.name} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={inviteFormData.is_staff}
                  onChange={(e) => setInviteFormData({ ...inviteFormData, is_staff: e.target.checked })}
                />
              }
              label="Admin Access (Staff)"
            />
          </Grid>
        </Grid>
      </Modal>

      {/* Edit User Modal */}
      <Modal
        open={editFormOpen}
        onClose={() => setEditFormOpen(false)}
        title="Edit User"
        maxWidth="sm"
        actions={
          <>
            <Button variant="outlined" onClick={() => setEditFormOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveEdit} loading={saving}>Update</Button>
          </>
        }
      >
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Department</InputLabel>
              <Select
                value={editFormData.department}
                onChange={(e) => setEditFormData({ ...editFormData, department: e.target.value })}
                label="Department"
              >
                <MenuItem value="">None</MenuItem>
                {departments.map((d) => (
                  <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Job Title"
              value={editFormData.job_title}
              onChange={(e) => setEditFormData({ ...editFormData, job_title: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Phone Number"
              value={editFormData.phone_number}
              onChange={(e) => setEditFormData({ ...editFormData, phone_number: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={editFormData.is_technician}
                  onChange={(e) => setEditFormData({ ...editFormData, is_technician: e.target.checked })}
                />
              }
              label="Is Technician"
            />
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>Roles</InputLabel>
              <Select
                multiple
                value={editFormData.role_ids}
                onChange={(e) => setEditFormData({ ...editFormData, role_ids: e.target.value })}
                input={<OutlinedInput label="Roles" />}
                renderValue={(selected) => roles.filter((r) => selected.includes(r.id)).map((r) => r.name).join(', ')}
              >
                {roles.map((role) => (
                  <MenuItem key={role.id} value={role.id}>
                    <Checkbox checked={editFormData.role_ids.includes(role.id)} />
                    <ListItemText primary={role.name} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Modal>

      {/* Delete User Modal */}
      <Modal
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        title="Delete User"
        maxWidth="xs"
        actions={
          <>
            <Button variant="outlined" onClick={() => setDeleteModalOpen(false)}>Cancel</Button>
            <Button color="error" onClick={handleDelete}>Delete</Button>
          </>
        }
      >
        <Typography>Are you sure you want to delete "{selectedUser?.user?.username}"?</Typography>
      </Modal>
    </>
  )
}

// Department Management Tab
const DepartmentsTab = () => {
  const [departments, setDepartments] = useState([])
  const [loading, setLoading] = useState(true)
  const [formOpen, setFormOpen] = useState(false)
  const [selectedDept, setSelectedDept] = useState(null)
  const [formData, setFormData] = useState({ name: '', code: '', description: '', is_active: true })
  const [saving, setSaving] = useState(false)

  const fetchData = async () => {
    try {
      const response = await api.get('/common/departments/')
      setDepartments(response.data.results || response.data)
    } catch (error) {
      console.error('Error fetching departments:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [])

  const handleAdd = () => {
    setSelectedDept(null)
    setFormData({ name: '', code: '', description: '', is_active: true })
    setFormOpen(true)
  }

  const handleEdit = (dept) => {
    setSelectedDept(dept)
    setFormData({
      name: dept.name,
      code: dept.code,
      description: dept.description || '',
      is_active: dept.is_active,
    })
    setFormOpen(true)
  }

  const handleSave = async () => {
    setSaving(true)
    try {
      if (selectedDept) {
        await api.patch(`/common/departments/${selectedDept.id}/`, formData)
        toast.success('Department updated successfully')
      } else {
        await api.post('/common/departments/', formData)
        toast.success('Department created successfully')
      }
      setFormOpen(false)
      fetchData()
    } catch (error) {
      console.error('Error saving department:', error)
      const errorMessage = error.response?.data?.error
        || error.response?.data?.detail
        || error.response?.data?.code?.[0]
        || error.response?.data?.name?.[0]
        || 'Failed to save department. Please try again.'
      toast.error(errorMessage)
    } finally {
      setSaving(false)
    }
  }

  const columns = [
    { field: 'name', headerName: 'Name', sortable: true },
    { field: 'code', headerName: 'Code', sortable: true },
    { field: 'description', headerName: 'Description', renderCell: (v) => v || '-' },
    { field: 'member_count', headerName: 'Members' },
    {
      field: 'is_active',
      headerName: 'Status',
      renderCell: (value) => <StatusChip status={value ? 'active' : 'inactive'} />,
    },
    {
      field: 'actions',
      headerName: '',
      renderCell: (_, row) => (
        <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleEdit(row) }}>
          <Edit fontSize="small" />
        </IconButton>
      ),
    },
  ]

  return (
    <>
      <Box display="flex" justifyContent="flex-end" mb={2}>
        <Button startIcon={<Add />} onClick={handleAdd}>Add Department</Button>
      </Box>
      <DataTable
        columns={columns}
        data={departments}
        loading={loading}
        searchPlaceholder="Search departments..."
        emptyMessage="No departments found"
      />

      <Modal
        open={formOpen}
        onClose={() => setFormOpen(false)}
        title={selectedDept ? 'Edit Department' : 'Add Department'}
        maxWidth="sm"
        actions={
          <>
            <Button variant="outlined" onClick={() => setFormOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} loading={saving}>{selectedDept ? 'Update' : 'Create'}</Button>
          </>
        }
      >
        <Grid container spacing={2}>
          <Grid item xs={12} sm={8}>
            <TextField
              fullWidth
              label="Department Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Code"
              value={formData.code}
              onChange={(e) => setFormData({ ...formData, code: e.target.value })}
              required
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              multiline
              rows={2}
            />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                />
              }
              label="Active"
            />
          </Grid>
        </Grid>
      </Modal>
    </>
  )
}

// Roles Management Tab
const RolesTab = () => {
  const [roles, setRoles] = useState([])
  const [availablePermissions, setAvailablePermissions] = useState({})
  const [permissionCategories, setPermissionCategories] = useState({})
  const [loading, setLoading] = useState(true)
  const [formOpen, setFormOpen] = useState(false)
  const [selectedRole, setSelectedRole] = useState(null)
  const [formData, setFormData] = useState({ name: '', description: '', is_active: true, permissions: [] })
  const [saving, setSaving] = useState(false)
  const [expandedCategories, setExpandedCategories] = useState({})

  const fetchData = async () => {
    try {
      const [rolesRes, permsRes] = await Promise.all([
        api.get('/common/roles/'),
        api.get('/auth/permissions/available/'),
      ])
      setRoles(rolesRes.data.results || rolesRes.data)
      setAvailablePermissions(permsRes.data.permissions || {})
      setPermissionCategories(permsRes.data.categories || {})
    } catch (error) {
      console.error('Error fetching data:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [])

  const handleAdd = () => {
    setSelectedRole(null)
    setFormData({ name: '', description: '', is_active: true, permissions: [] })
    setExpandedCategories({})
    setFormOpen(true)
  }

  const handleEdit = (role) => {
    setSelectedRole(role)
    setFormData({
      name: role.name,
      description: role.description || '',
      is_active: role.is_active,
      permissions: role.permissions || [],
    })
    setExpandedCategories({})
    setFormOpen(true)
  }

  const handleSave = async () => {
    setSaving(true)
    try {
      if (selectedRole) {
        await api.patch(`/common/roles/${selectedRole.id}/`, formData)
        toast.success('Role updated successfully')
      } else {
        await api.post('/common/roles/', formData)
        toast.success('Role created successfully')
      }
      setFormOpen(false)
      fetchData()
    } catch (error) {
      console.error('Error saving role:', error)
      const errorMessage = error.response?.data?.error
        || error.response?.data?.detail
        || error.response?.data?.name?.[0]
        || 'Failed to save role. Please try again.'
      toast.error(errorMessage)
    } finally {
      setSaving(false)
    }
  }

  const togglePermission = (permCode) => {
    setFormData((prev) => ({
      ...prev,
      permissions: prev.permissions.includes(permCode)
        ? prev.permissions.filter((p) => p !== permCode)
        : [...prev.permissions, permCode],
    }))
  }

  const toggleCategory = (categoryPerms) => {
    const permCodes = categoryPerms.map((p) => p.code)
    const allSelected = permCodes.every((p) => formData.permissions.includes(p))
    if (allSelected) {
      setFormData((prev) => ({
        ...prev,
        permissions: prev.permissions.filter((p) => !permCodes.includes(p)),
      }))
    } else {
      setFormData((prev) => ({
        ...prev,
        permissions: [...new Set([...prev.permissions, ...permCodes])],
      }))
    }
  }

  const columns = [
    { field: 'name', headerName: 'Role Name', sortable: true },
    { field: 'description', headerName: 'Description', renderCell: (v) => v || '-' },
    {
      field: 'permissions',
      headerName: 'Permissions',
      renderCell: (value) => (
        <Chip label={`${value?.length || 0} permissions`} size="small" variant="outlined" />
      ),
    },
    {
      field: 'is_active',
      headerName: 'Status',
      renderCell: (value) => <StatusChip status={value ? 'active' : 'inactive'} />,
    },
    {
      field: 'actions',
      headerName: '',
      renderCell: (_, row) => (
        <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleEdit(row) }}>
          <Edit fontSize="small" />
        </IconButton>
      ),
    },
  ]

  return (
    <>
      <Box display="flex" justifyContent="flex-end" mb={2}>
        <Button startIcon={<Add />} onClick={handleAdd}>Add Role</Button>
      </Box>
      <DataTable
        columns={columns}
        data={roles}
        loading={loading}
        searchPlaceholder="Search roles..."
        emptyMessage="No roles found"
      />

      <Modal
        open={formOpen}
        onClose={() => setFormOpen(false)}
        title={selectedRole ? 'Edit Role' : 'Add Role'}
        maxWidth="md"
        actions={
          <>
            <Button variant="outlined" onClick={() => setFormOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} loading={saving}>{selectedRole ? 'Update' : 'Create'}</Button>
          </>
        }
      >
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Role Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                />
              }
              label="Active"
              sx={{ mt: 1 }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              multiline
              rows={2}
            />
          </Grid>
          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />
            <Typography variant="subtitle1" fontWeight="bold" mb={2}>
              Permissions ({formData.permissions.length} selected)
            </Typography>
            <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
              {Object.entries(permissionCategories).map(([category, perms]) => {
                const allSelected = perms.every((p) => formData.permissions.includes(p.code))
                const someSelected = perms.some((p) => formData.permissions.includes(p.code))
                return (
                  <Box key={category} mb={2}>
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      <Checkbox
                        checked={allSelected}
                        indeterminate={someSelected && !allSelected}
                        onChange={() => toggleCategory(perms)}
                      />
                      <Typography
                        variant="subtitle2"
                        fontWeight="bold"
                        sx={{ cursor: 'pointer' }}
                        onClick={() => setExpandedCategories((prev) => ({
                          ...prev,
                          [category]: !prev[category],
                        }))}
                      >
                        {category} ({perms.filter((p) => formData.permissions.includes(p.code)).length}/{perms.length})
                      </Typography>
                    </Box>
                    <Box pl={4}>
                      {perms.map((perm) => (
                        <FormControlLabel
                          key={perm.code}
                          control={
                            <Checkbox
                              size="small"
                              checked={formData.permissions.includes(perm.code)}
                              onChange={() => togglePermission(perm.code)}
                            />
                          }
                          label={
                            <Typography variant="body2">
                              {perm.name} <Typography component="span" variant="caption" color="text.secondary">({perm.code})</Typography>
                            </Typography>
                          }
                        />
                      ))}
                    </Box>
                  </Box>
                )
              })}
            </Box>
          </Grid>
        </Grid>
      </Modal>
    </>
  )
}

// Profile Settings Tab
const ProfileTab = () => {
  const { user, refreshProfile } = useAuth()
  const [notifications, setNotifications] = useState({
    emailWorkOrders: true,
    emailMaintenanceDue: true,
    emailInventoryAlerts: true,
    pushWorkOrders: true,
    pushMaintenanceDue: true,
  })
  const [editProfileOpen, setEditProfileOpen] = useState(false)
  const [changePasswordOpen, setChangePasswordOpen] = useState(false)
  const [saving, setSaving] = useState(false)
  const [profileFormData, setProfileFormData] = useState({
    first_name: '',
    last_name: '',
    phone_number: '',
  })
  const [passwordFormData, setPasswordFormData] = useState({
    old_password: '',
    new_password: '',
    confirm_password: '',
  })

  useEffect(() => {
    if (user) {
      setProfileFormData({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        phone_number: user.phone_number || '',
      })
    }
  }, [user])

  useEffect(() => {
    const loadPreferences = async () => {
      try {
        const response = await api.get('/notifications/preferences/')
        setNotifications({
          emailWorkOrders: response.data.email_work_orders ?? true,
          emailMaintenanceDue: response.data.email_maintenance_due ?? true,
          emailInventoryAlerts: response.data.email_inventory_alerts ?? true,
          pushWorkOrders: response.data.push_work_orders ?? true,
          pushMaintenanceDue: response.data.push_maintenance_due ?? true,
        })
      } catch (error) {
        console.error('Error loading preferences:', error)
      }
    }
    loadPreferences()
  }, [])

  const handleNotificationChange = (key) => {
    setNotifications((prev) => ({ ...prev, [key]: !prev[key] }))
  }

  const handleSavePreferences = async () => {
    setSaving(true)
    try {
      await api.patch('/notifications/preferences/', {
        email_work_orders: notifications.emailWorkOrders,
        email_maintenance_due: notifications.emailMaintenanceDue,
        email_inventory_alerts: notifications.emailInventoryAlerts,
        push_work_orders: notifications.pushWorkOrders,
        push_maintenance_due: notifications.pushMaintenanceDue,
      })
      toast.success('Preferences saved successfully')
    } catch (error) {
      toast.error('Failed to save preferences')
    } finally {
      setSaving(false)
    }
  }

  const handleOpenEditProfile = () => {
    setProfileFormData({
      first_name: user?.first_name || '',
      last_name: user?.last_name || '',
      phone_number: user?.phone_number || '',
    })
    setEditProfileOpen(true)
  }

  const handleSaveProfile = async () => {
    setSaving(true)
    try {
      await api.patch('/auth/profile/', profileFormData)
      toast.success('Profile updated successfully')
      setEditProfileOpen(false)
      if (refreshProfile) {
        await refreshProfile()
      }
    } catch (error) {
      toast.error('Failed to update profile')
    } finally {
      setSaving(false)
    }
  }

  const handleOpenChangePassword = () => {
    setPasswordFormData({
      old_password: '',
      new_password: '',
      confirm_password: '',
    })
    setChangePasswordOpen(true)
  }

  const handleChangePassword = async () => {
    if (!passwordFormData.old_password || !passwordFormData.new_password) {
      toast.error('Please fill in all fields')
      return
    }
    if (passwordFormData.new_password !== passwordFormData.confirm_password) {
      toast.error('New passwords do not match')
      return
    }
    if (passwordFormData.new_password.length < 8) {
      toast.error('Password must be at least 8 characters')
      return
    }
    setSaving(true)
    try {
      await api.post('/auth/change-password/', {
        old_password: passwordFormData.old_password,
        new_password: passwordFormData.new_password,
      })
      toast.success('Password changed successfully')
      setChangePasswordOpen(false)
    } catch (error) {
      const errorMessage = error.response?.data?.old_password?.[0]
        || error.response?.data?.error
        || 'Failed to change password'
      toast.error(errorMessage)
    } finally {
      setSaving(false)
    }
  }

  return (
    <>
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card title="Profile Information">
            <Box display="flex" flexDirection="column" gap={2}>
              <Box>
                <Typography variant="caption" color="text.secondary">Username</Typography>
                <Typography variant="body1">{user?.username}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Email</Typography>
                <Typography variant="body1">{user?.email || '-'}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Name</Typography>
                <Typography variant="body1">
                  {user?.first_name ? `${user.first_name} ${user.last_name || ''}` : '-'}
                </Typography>
              </Box>
              <Divider sx={{ my: 1 }} />
              <Button variant="outlined" onClick={handleOpenEditProfile}>Edit Profile</Button>
            </Box>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card title="Notification Preferences">
            <Box display="flex" flexDirection="column" gap={1}>
              <Typography variant="subtitle2" color="text.secondary" mb={1}>Email Notifications</Typography>
              <FormControlLabel
                control={<Switch checked={notifications.emailWorkOrders} onChange={() => handleNotificationChange('emailWorkOrders')} />}
                label="Work order updates"
              />
              <FormControlLabel
                control={<Switch checked={notifications.emailMaintenanceDue} onChange={() => handleNotificationChange('emailMaintenanceDue')} />}
                label="Maintenance reminders"
              />
              <FormControlLabel
                control={<Switch checked={notifications.emailInventoryAlerts} onChange={() => handleNotificationChange('emailInventoryAlerts')} />}
                label="Inventory alerts"
              />
              <Typography variant="subtitle2" color="text.secondary" mt={2} mb={1}>Push Notifications</Typography>
              <FormControlLabel
                control={<Switch checked={notifications.pushWorkOrders} onChange={() => handleNotificationChange('pushWorkOrders')} />}
                label="Work order updates"
              />
              <FormControlLabel
                control={<Switch checked={notifications.pushMaintenanceDue} onChange={() => handleNotificationChange('pushMaintenanceDue')} />}
                label="Maintenance reminders"
              />
              <Box mt={2}>
                <Button onClick={handleSavePreferences} loading={saving}>Save Preferences</Button>
              </Box>
            </Box>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card title="Security">
            <Button variant="outlined" onClick={handleOpenChangePassword}>Change Password</Button>
          </Card>
        </Grid>
      </Grid>

      {/* Edit Profile Modal */}
      <Modal
        open={editProfileOpen}
        onClose={() => setEditProfileOpen(false)}
        title="Edit Profile"
        maxWidth="sm"
        actions={
          <>
            <Button variant="outlined" onClick={() => setEditProfileOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveProfile} loading={saving}>Save</Button>
          </>
        }
      >
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="First Name"
              value={profileFormData.first_name}
              onChange={(e) => setProfileFormData({ ...profileFormData, first_name: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Last Name"
              value={profileFormData.last_name}
              onChange={(e) => setProfileFormData({ ...profileFormData, last_name: e.target.value })}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Phone Number"
              value={profileFormData.phone_number}
              onChange={(e) => setProfileFormData({ ...profileFormData, phone_number: e.target.value })}
            />
          </Grid>
        </Grid>
      </Modal>

      {/* Change Password Modal */}
      <Modal
        open={changePasswordOpen}
        onClose={() => setChangePasswordOpen(false)}
        title="Change Password"
        maxWidth="sm"
        actions={
          <>
            <Button variant="outlined" onClick={() => setChangePasswordOpen(false)}>Cancel</Button>
            <Button onClick={handleChangePassword} loading={saving}>Change Password</Button>
          </>
        }
      >
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              type="password"
              label="Current Password"
              value={passwordFormData.old_password}
              onChange={(e) => setPasswordFormData({ ...passwordFormData, old_password: e.target.value })}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              type="password"
              label="New Password"
              value={passwordFormData.new_password}
              onChange={(e) => setPasswordFormData({ ...passwordFormData, new_password: e.target.value })}
              helperText="Must be at least 8 characters"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              type="password"
              label="Confirm New Password"
              value={passwordFormData.confirm_password}
              onChange={(e) => setPasswordFormData({ ...passwordFormData, confirm_password: e.target.value })}
            />
          </Grid>
        </Grid>
      </Modal>
    </>
  )
}

const Settings = () => {
  const [activeTab, setActiveTab] = useState(0)
  const { user } = useAuth()
  const isAdmin = user?.is_superuser || user?.is_staff

  return (
    <Box>
      <PageHeader
        title="Settings"
        subtitle="Manage system settings, users, and preferences"
        breadcrumbs={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'Settings' },
        ]}
      />

      <Card>
        <Tabs
          value={activeTab}
          onChange={(_, newValue) => setActiveTab(newValue)}
          sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab icon={<Person />} iconPosition="start" label="Users" disabled={!isAdmin} />
          <Tab icon={<Business />} iconPosition="start" label="Departments" disabled={!isAdmin} />
          <Tab icon={<Security />} iconPosition="start" label="Roles" disabled={!isAdmin} />
          <Tab icon={<SettingsIcon />} iconPosition="start" label="My Profile" />
        </Tabs>

        {activeTab === 0 && isAdmin && <UsersTab />}
        {activeTab === 1 && isAdmin && <DepartmentsTab />}
        {activeTab === 2 && isAdmin && <RolesTab />}
        {activeTab === 3 && <ProfileTab />}
        {!isAdmin && activeTab < 3 && (
          <Box textAlign="center" py={4}>
            <Typography color="text.secondary">
              You need administrator privileges to access this section.
            </Typography>
          </Box>
        )}
      </Card>
    </Box>
  )
}

export default Settings
